import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageIntro } from '@/components/help/PageIntro';
import { LabelWithHint } from '@/components/help/LabelWithHint';
import { PlatformIcon } from '@/components/social/PlatformIcon';
import { RateLimitBanner } from '@/components/social/RateLimitBanner';
import { BrowserPublishingPanel } from '@/components/social/BrowserPublishingPanel';
import { Button } from '@/components/ui/button';
import {
  socialApi,
  type AnalyticsSummary,
  type SocialAccount,
  type SocialPlatform,
  type SocialPost,
  type SocialSettingsInfo,
  type SocialTargetStatus,
} from '@/lib/social-api';
import { PLATFORM_ORDER, platformMeta } from '@/lib/social-platforms';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Link2,
  Loader2,
  Lock,
  RefreshCw,
  RotateCcw,
  Trash2,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { oauthErrorMessage } from '@/lib/oauth-errors';
import { consumeHashOAuthFeedback, getHashSearchParams } from '@/lib/hash-route';

type HubTab = 'accounts' | 'queue' | 'analytics' | 'browser';

interface SocialHubPageProps {
  onNavigate: (path: string) => void;
  initialTab?: HubTab | 'overview';
}

interface QueueRow {
  targetId: string;
  postId: string;
  platform: SocialPlatform;
  status: SocialTargetStatus;
  preview: string;
  scheduledAt?: string | null;
  publishedAt?: string | null;
  errorMessage?: string | null;
  externalUrl?: string | null;
  accountName: string;
}

export function SocialHubPage({ onNavigate, initialTab }: SocialHubPageProps) {
  const params = getHashSearchParams();
  const platformParam = (params.get('platform') as SocialPlatform | null) || null;

  const [tab, setTab] = useState<HubTab>(
    initialTab && initialTab !== 'overview' ? initialTab : 'accounts'
  );
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const [connecting, setConnecting] = useState<SocialPlatform | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SocialTargetStatus | null>(null);
  const [settings, setSettings] = useState<SocialSettingsInfo | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [maintaining, setMaintaining] = useState(false);
  const [platformFilter, setPlatformFilter] = useState<SocialPlatform | null>(platformParam);
  const [providerConfigured, setProviderConfigured] = useState<Partial<Record<SocialPlatform, boolean>>>(
    {}
  );

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [acc, list, cfg, stats, providers] = await Promise.allSettled([
        socialApi.listAccounts(),
        socialApi.listPosts({ all: true }),
        socialApi.settings().catch(() => null),
        socialApi.analyticsSummary(30).catch(() => null),
        socialApi.listProviders(),
      ]);
      if (acc.status === 'fulfilled') setAccounts(acc.value);
      if (list.status === 'fulfilled') setPosts(list.value);
      if (cfg.status === 'fulfilled') setSettings(cfg.value);
      if (stats.status === 'fulfilled') setSummary(stats.value);
      if (providers.status === 'fulfilled') {
        const map: Partial<Record<SocialPlatform, boolean>> = {};
        for (const p of providers.value) map[p.platform] = p.configured;
        setProviderConfigured(map);
      } else {
        setProviderConfigured({});
      }
      const failed = [acc, list, providers].find((result) => result.status === 'rejected');
      if (failed?.status === 'rejected') {
        setLoadError(failed.reason instanceof Error ? failed.reason.message : 'Ein Teil der Daten konnte nicht geladen werden.');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Social Hub konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (initialTab && initialTab !== 'overview') setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (tab !== 'queue') return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [tab, load]);

  // OAuth-Rückmeldung aus dem Callback einmalig anzeigen.
  useEffect(() => {
    const { error, connected } = consumeHashOAuthFeedback();
    if (error) toast.error(oauthErrorMessage(error), { id: `oauth-${error}` });
    else if (connected) toast.success('Kanal verbunden.', { id: 'oauth-connected' });
  }, []);

  const queue = useMemo<QueueRow[]>(() => {
    const rows: QueueRow[] = [];
    for (const post of posts) {
      for (const target of post.targets) {
        rows.push({
          targetId: target.id,
          postId: post.id,
          platform: target.platform,
          status: target.status,
          preview: (post.title || post.body || 'Ohne Text').slice(0, 90),
          scheduledAt: target.scheduled_at,
          publishedAt: target.published_at,
          errorMessage: target.error_message,
          externalUrl: target.external_url,
          accountName: target.social_accounts?.display_name ?? '',
        });
      }
    }
    return rows.sort((a, b) => {
      const av = a.scheduledAt || a.publishedAt || '';
      const bv = b.scheduledAt || b.publishedAt || '';
      return bv.localeCompare(av);
    });
  }, [posts]);

  const filteredQueue = useMemo(
    () =>
      queue.filter(
        (r) =>
          (!statusFilter || r.status === statusFilter) &&
          (!platformFilter || r.platform === platformFilter)
      ),
    [queue, statusFilter, platformFilter]
  );

  const counts = useMemo(() => {
    const base: Record<SocialTargetStatus, number> = {
      draft: 0,
      pending_approval: 0,
      scheduled: 0,
      publishing: 0,
      published: 0,
      failed: 0,
      cancelled: 0,
    };
    for (const row of queue) base[row.status] += 1;
    return base;
  }, [queue]);

  const unhealthy = accounts.filter((a) => a.status !== 'active');
  const healthBanner = unhealthy.length
    ? `Diese Kanäle brauchen Aufmerksamkeit: ${unhealthy
        .map((a) => `${platformMeta(a.platform).label} (${a.display_name || a.status})`)
        .join(', ')}. Vor dem nächsten Publish neu verbinden.`
    : null;

  const connect = async (platform: SocialPlatform) => {
    if (providerConfigured[platform] !== true) {
      toast.error(providerConfigured[platform] === false
        ? oauthErrorMessage('provider_not_configured')
        : 'Verbindungsstatus noch nicht verfügbar. Bitte aktualisieren.');
      return;
    }
    setConnecting(platform);
    try {
      const { url } = await socialApi.getAuthorizeUrl(
        platform,
        platform === 'linkedin' ? { type: 'company' } : undefined
      );
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'OAuth konnte nicht gestartet werden.');
      setConnecting(null);
    }
  };

  const disconnect = async (id: string) => {
    setBusyId(id);
    try {
      await socialApi.disconnectAccount(id);
      toast.success('Kanal getrennt.');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Trennen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const reauth = async (account: SocialAccount) => {
    setBusyId(account.id);
    try {
      await socialApi.refreshAccount(account.id);
      toast.success('Token erneuert.');
      await load();
    } catch {
      toast.message('Automatischer Refresh nicht möglich — Kanal wird neu verbunden.');
      await connect(account.platform);
    } finally {
      setBusyId(null);
    }
  };

  const retryTarget = async (targetId: string) => {
    setBusyId(targetId);
    try {
      await socialApi.retryTarget(targetId);
      toast.success('Erneuter Versuch gestartet.');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const retryAll = async () => {
    setRetryingAll(true);
    try {
      const { queued } = await socialApi.retryAllFailed();
      toast.success(`${queued} Kanäle erneut eingereiht.`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Sammel-Retry fehlgeschlagen.');
    } finally {
      setRetryingAll(false);
    }
  };

  /**
   * Gleicht Datenbank und Queue ab: überfällige Termine werden nachgeholt,
   * hängende Publishes als fehlgeschlagen markiert.
   */
  const reconcile = async () => {
    setMaintaining(true);
    try {
      const result = await socialApi.reconcileQueue();
      const total = result.requeued + result.rescheduled + result.stuck;
      toast.success(
        total === 0
          ? 'Queue ist synchron – nichts zu tun.'
          : `Abgeglichen: ${result.requeued} nachgeholt, ${result.rescheduled} neu terminiert, ${result.stuck} abgebrochen.`
      );
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Abgleich fehlgeschlagen.');
    } finally {
      setMaintaining(false);
    }
  };

  const syncAnalytics = async () => {
    setMaintaining(true);
    try {
      const result = await socialApi.syncAnalytics();
      toast.success(`${result.updated} Beiträge aktualisiert (${result.checked} geprüft).`);
      setSummary(await socialApi.analyticsSummary(30).catch(() => null));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Analytics-Sync fehlgeschlagen.');
    } finally {
      setMaintaining(false);
    }
  };

  const cancelTarget = async (targetId: string) => {
    setBusyId(targetId);
    try {
      await socialApi.cancelTarget(targetId);
      toast.success('Geplante Veröffentlichung abgebrochen.');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Abbrechen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" />
      </div>
    );
  }

  const tabs: Array<{ id: HubTab; label: string }> = [
    { id: 'accounts', label: 'Kanäle' },
    { id: 'queue', label: `Warteschlange${counts.failed ? ` (${counts.failed} Fehler)` : ''}` },
    { id: 'analytics', label: 'Analytics' },
    { id: 'browser', label: 'Browser' },
  ];

  return (
    <div className="w-full">
      <PageIntro
        title="Social Hub"
        description="Kanäle verbinden, Veröffentlichungen prüfen und Fehler beheben."
        className="max-w-none"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void load()}>
              <RefreshCw size={16} className="mr-1.5" /> Aktualisieren
            </Button>
            <Button variant="outline" onClick={() => onNavigate('/planner')}>
              Content-Planner
            </Button>
            <Button onClick={() => onNavigate('/composer')}>Neuer Post</Button>
          </div>
        }
      />

      <RateLimitBanner message={healthBanner} />

      {loadError && (
        <p role="alert" className="mb-4 rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          Daten sind unvollständig oder veraltet: {loadError}
        </p>
      )}

      <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Aktive Kanäle" value={accounts.filter((a) => a.status === 'active').length} />
        <StatCard label="Geplant" value={counts.scheduled} />
        <StatCard label="Veröffentlicht" value={counts.published} />
        <StatCard label="Fehler" value={counts.failed} tone={counts.failed ? 'danger' : 'default'} />
      </div>

      <div className="mb-6 flex flex-wrap gap-2 border-b border-border pb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'rounded-lg px-3 py-1.5 text-sm font-medium transition',
              tab === t.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted hover:bg-card-hover'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'browser' && <BrowserPublishingPanel />}

      {tab === 'accounts' && (
        <div className="space-y-6">
          <section>
            <LabelWithHint
              label="Kanal verbinden"
              hint="Einmal die Plattform-App einrichten, danach das Konto per OAuth verbinden."
            />
            {Object.values(providerConfigured).some((v) => v === false) && (
              <p className="mt-2 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted">
                Für gesperrte Kanäle fehlen die App-Zugangsdaten. Einrichten unter{' '}
                <a href="#/admin/integrations" className="text-foreground underline underline-offset-2">
                  Admin → Integrationen
                </a>.
              </p>
            )}
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {PLATFORM_ORDER.map((platform) => {
                const meta = platformMeta(platform);
                const connected = accounts.filter((a) => a.platform === platform && a.status === 'active');
                const configured = providerConfigured[platform] === true;
                const unknown = providerConfigured[platform] === undefined;
                const busy = connecting === platform;
                return (
                  <button
                    key={platform}
                    type="button"
                    disabled={!!connecting || !configured}
                    onClick={() => void connect(platform)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition',
                      configured
                        ? 'hover:border-foreground/20 hover:bg-card-hover'
                        : 'cursor-not-allowed opacity-70',
                      busy && 'opacity-60'
                    )}
                  >
                    <PlatformIcon platform={platform} size={40} muted={!configured} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">{meta.label}</span>
                      <span className="mt-0.5 block text-xs text-muted">
                        {unknown
                          ? 'Verbindungsstatus nicht verfügbar'
                          : !configured
                          ? 'Einrichtung erforderlich'
                          : connected.length
                            ? `${connected.length} verbunden`
                            : 'Nicht verbunden'}
                      </span>
                      {configured && (
                        <span className="mt-1 block text-xs text-muted/90 line-clamp-2">{meta.hint}</span>
                      )}
                    </span>
                    {busy ? (
                      <Loader2 size={15} className="mt-1 shrink-0 animate-spin text-foreground" />
                    ) : !configured ? (
                      <Lock size={15} className="mt-1 shrink-0 text-muted" />
                    ) : (
                      <Link2 size={15} className="mt-1 shrink-0 text-muted" />
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <LabelWithHint
              label="Verbundene Konten"
              hint="Trennen entfernt nur den App-Zugriff, der Kanal selbst bleibt bestehen. Abgelaufene Tokens müssen neu autorisiert werden."
            />
            <div className="mt-3 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {accounts.length === 0 && (
                <p className="p-6 text-sm text-muted">Noch keine Kanäle verbunden.</p>
              )}
              {accounts.map((acc) => {
                const meta = platformMeta(acc.platform);
                const broken = acc.status !== 'active';
                return (
                  <div key={acc.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <PlatformIcon platform={acc.platform} size={32} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">
                        {acc.display_name}
                        <span className="ml-2 text-xs font-normal text-muted">{meta.label}</span>
                      </p>
                      <p className="flex items-center gap-1 text-xs text-muted">
                        {broken ? (
                          <AlertTriangle size={11} className="text-warning" />
                        ) : (
                          <CheckCircle2 size={11} className="text-success" />
                        )}
                        {ACCOUNT_STATUS_LABELS[acc.status]}
                        {acc.token_expires_at &&
                          ` · gültig bis ${new Date(acc.token_expires_at).toLocaleDateString('de-CH')}`}
                      </p>
                    </div>
                    {broken && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === acc.id}
                        onClick={() => void reauth(acc)}
                      >
                        <RotateCcw size={13} className="mr-1" /> Neu verbinden
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === acc.id}
                      onClick={() => void disconnect(acc.id)}
                    >
                      <Trash2 size={13} className="mr-1" /> Trennen
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      {tab === 'queue' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1">
              {([
                'scheduled',
                'pending_approval',
                'publishing',
                'published',
                'failed',
                'draft',
                'cancelled',
              ] as SocialTargetStatus[]).map(
                (s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                    className={cn(
                      'rounded-lg border px-2.5 py-1 text-xs font-medium transition',
                      statusFilter === s
                        ? 'border-border bg-primary text-primary-foreground'
                        : 'border-border text-muted'
                    )}
                  >
                    {TARGET_STATUS_LABELS[s]} ({counts[s]})
                  </button>
                )
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {PLATFORM_ORDER.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatformFilter(platformFilter === p ? null : p)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium transition',
                    platformFilter === p
                      ? 'border-border bg-primary text-primary-foreground'
                      : 'border-border text-muted'
                  )}
                >
                  <PlatformIcon platform={p} size={16} />
                  {platformMeta(p).label}
                </button>
              ))}
              {counts.failed > 0 && (
                <Button variant="outline" size="sm" className="ml-2" disabled={retryingAll} onClick={() => void retryAll()}>
                  <RotateCcw size={13} className="mr-1.5" /> Alle Fehler erneut
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => void reconcile()} disabled={maintaining}>
                {maintaining ? (
                  <Loader2 size={13} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={13} className="mr-1.5" />
                )}
                Queue abgleichen
              </Button>
            </div>
          </div>

          {settings && !settings.queue_available && (
            <p className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-2 text-xs text-warning">
              Planung läuft im Ersatzbetrieb. Geplante Beiträge können mit etwa einer Minute Verzögerung erscheinen.
            </p>
          )}

          {settings && (
            <p className="text-xs text-muted">
              Zeitzone {settings.timezone}
              {settings.quiet_hours
                ? ` · Ruhezeit ${settings.quiet_hours.from}–${settings.quiet_hours.to} (Termine werden automatisch verschoben)`
                : ' · keine Ruhezeit gesetzt'}
              {settings.require_approval ? ' · Freigabe erforderlich' : ''}
              {settings.link_tracking ? ' · UTM-Tracking aktiv' : ''}
            </p>
          )}

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-background text-xs uppercase text-muted dark:bg-card">
                <tr>
                  <th className="px-4 py-3">Inhalt</th>
                  <th className="px-4 py-3">Kanal</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Termin</th>
                  <th className="px-4 py-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueue.map((row) => (
                  <tr key={row.targetId} className="border-t border-border">
                    <td className="max-w-xs px-4 py-3">
                      <button
                        type="button"
                        className="truncate text-left text-foreground hover:underline"
                        onClick={() => onNavigate(`/composer/${row.postId}`)}
                      >
                        {row.preview}
                      </button>
                      {row.errorMessage && (
                        <p className="mt-1 truncate text-xs text-danger" title={row.errorMessage}>
                          {row.errorMessage}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2">
                        <PlatformIcon platform={row.platform} size={22} />
                        <span>
                          <span className="text-xs font-medium text-foreground">
                            {platformMeta(row.platform).label}
                          </span>
                          {row.accountName && (
                            <span className="ml-1.5 text-xs text-muted">{row.accountName}</span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={row.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted">
                      {row.publishedAt
                        ? new Date(row.publishedAt).toLocaleString('de-CH')
                        : row.scheduledAt
                          ? new Date(row.scheduledAt).toLocaleString('de-CH')
                          : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {row.externalUrl && (
                          <a
                            href={row.externalUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="rounded p-1.5 text-muted hover:bg-card-hover hover:text-foreground"
                            title="Beitrag öffnen"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                        {row.status === 'failed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === row.targetId}
                            onClick={() => void retryTarget(row.targetId)}
                          >
                            <RotateCcw size={13} className="mr-1" /> Retry
                          </Button>
                        )}
                        {row.status === 'scheduled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === row.targetId}
                            onClick={() => void cancelTarget(row.targetId)}
                          >
                            <XCircle size={13} className="mr-1" /> Abbrechen
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredQueue.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted">
                      Nichts in der Warteschlange.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'analytics' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted">
              Kennzahlen der letzten 30 Tage. Der Sync läuft stündlich und wird mit dem Alter des
              Beitrags seltener.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => void syncAnalytics()} disabled={maintaining}>
                {maintaining ? (
                  <Loader2 size={13} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={13} className="mr-1.5" />
                )}
                Jetzt aktualisieren
              </Button>
              <Button variant="outline" size="sm" onClick={() => onNavigate('/statistics')}>
                Zu Insights
              </Button>
            </div>
          </div>

          {!summary || summary.platforms.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted">
              Noch keine Kennzahlen vorhanden. Sobald Beiträge veröffentlicht und synchronisiert sind,
              erscheinen sie hier.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-background text-xs uppercase text-muted dark:bg-card">
                  <tr>
                    <th className="px-4 py-3">Kanal</th>
                    <th className="px-4 py-3 text-right">Beiträge</th>
                    <th className="px-4 py-3 text-right">Impressionen</th>
                    <th className="px-4 py-3 text-right">Interaktionen</th>
                    <th className="px-4 py-3 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.platforms.map((row) => (
                    <tr key={row.platform} className="border-t border-border">
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <PlatformIcon platform={row.platform} size={22} />
                          <span className="text-sm font-medium text-foreground">
                            {platformMeta(row.platform).label}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{row.posts}</td>
                      <td className="px-4 py-3 text-right">{row.impressions.toLocaleString('de-CH')}</td>
                      <td className="px-4 py-3 text-right">{row.engagement.toLocaleString('de-CH')}</td>
                      <td className="px-4 py-3 text-right">
                        {row.engagement_rate != null ? `${row.engagement_rate}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: SocialTargetStatus }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        status === 'failed' && 'bg-danger-soft text-danger',
        status === 'published' && 'bg-success-soft text-success',
        status === 'scheduled' && 'bg-accent-soft text-accent',
        status === 'publishing' && 'bg-accent-soft text-accent-soft-foreground',
        status === 'pending_approval' &&
          'bg-warning-soft text-warning',
        (status === 'draft' || status === 'cancelled') &&
          'bg-card-hover text-muted'
      )}
    >
      {TARGET_STATUS_LABELS[status]}
    </span>
  );
}

const ACCOUNT_STATUS_LABELS: Record<SocialAccount['status'], string> = {
  active: 'Verbunden',
  expired: 'Verbindung abgelaufen',
  revoked: 'Zugriff widerrufen',
  error: 'Verbindungsfehler',
};

const TARGET_STATUS_LABELS: Record<SocialTargetStatus, string> = {
  draft: 'Entwurf',
  pending_approval: 'Freigabe offen',
  scheduled: 'Geplant',
  publishing: 'Wird veröffentlicht',
  published: 'Veröffentlicht',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
};

function StatCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'danger';
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p
        className={cn(
          'mt-1 text-3xl font-semibold tabular-nums',
          tone === 'danger' ? 'text-danger' : 'text-foreground'
        )}
      >
        {value}
      </p>
    </div>
  );
}
